/**
 * Landing pública de Kaptar (Sprint 7.5).
 *
 * Página sin auth en `/`. Presenta el producto al visitante (secretario de
 * planeación, alcalde, consultor) y lo empuja a autenticarse con un CTA
 * único. La lógica en App.tsx redirige a autenticados directo a /radar —
 * esta landing solo la ven no autenticados.
 *
 * Contenido intencionalmente breve. La landing "completa" con copy de
 * marketing, testimonios y precios vive eventualmente en un dominio
 * separado (kaptar.com.co) — el app.kaptar.com.co queda con esta vista mínima para
 * quien llega al dominio de la app sin sesión.
 */
import { Link } from "react-router-dom";

import { track } from "@/lib/analytics";

const PORTAL_SOURCES = [
  { code: "SGR", label: "Sistema General de Regalías" },
  { code: "APC", label: "APC Colombia (cooperación internacional)" },
  { code: "MinTIC", label: "Conectividad y TIC" },
  { code: "Mincit", label: "Turismo y comercio" },
  { code: "MinEnergía", label: "Electrificación y FAER" },
];

export function Landing() {
  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main className="flex-1">
        <Hero />
        <Value />
        <Sources />
        <Trust />
      </main>
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <div>
          <p className="text-xl font-extrabold tracking-tight text-brand-700">kaptar</p>
          <p className="text-[11px] font-medium text-slate-500">
            Inteligencia de recursos públicos
          </p>
        </div>
        <div className="flex items-center gap-4">
          <a
            href="https://kaptar.com.co"
            className="text-sm text-slate-500 transition-colors hover:text-brand-700"
          >
            Conoce más en kaptar.com.co →
          </a>
          <Link
            to="/login"
            onClick={() =>
              track({
                event_name: "landing_cta_clicked",
                properties: { cta_id: "header_login" },
              })
            }
            className="rounded-md border border-brand-700 px-4 py-2 text-sm font-semibold text-brand-700 hover:bg-brand-50"
          >
            Iniciar sesión
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="px-6 py-16 sm:py-24">
      <div className="mx-auto max-w-4xl text-center">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
          La verdad sobre los recursos de tu municipio.
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">
          Kaptar agrega todas las fuentes de financiación pública disponibles para tu municipio,
          filtra las convocatorias que realmente te aplican y te acompaña hasta la radicación en
          PIIP.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/login"
            onClick={() =>
              track({
                event_name: "landing_cta_clicked",
                properties: { cta_id: "hero_primary_login" },
              })
            }
            className="inline-flex items-center rounded-md bg-brand-700 px-6 py-3 text-sm font-semibold text-white shadow-xs hover:bg-brand-800"
          >
            Entrar al sistema →
          </Link>
        </div>
        <p className="mt-4 text-xs text-slate-500">Gratis durante el piloto · Hecho en Colombia</p>
      </div>
    </section>
  );
}

function Value() {
  const features = [
    {
      title: "Radar inteligente",
      body: "Todas las convocatorias abiertas, ordenadas por relevancia para tu municipio (PDET, región, sector con déficit). Cada tarjeta explica por qué te aplica.",
    },
    {
      title: "Expedientes sin fricción",
      body: "Creas el expediente desde la convocatoria y Kaptar siembra el checklist MGA + específicos del sector. Timeline de estados y radicación en PIIP guiada.",
    },
    {
      title: "Alertas y reporte de perdidas",
      body: "Te avisamos cuando aparece una convocatoria que aplica y cuando una que ya aplicas está por cerrar. Y al final del año ves cuánto recurso pasó por al lado.",
    },
  ];
  return (
    <section className="bg-white px-6 py-16">
      <div className="mx-auto max-w-6xl">
        <h2 className="text-center text-2xl font-bold text-slate-900 sm:text-3xl">
          Diseñado para los 1.100 municipios categorías 4-6
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-slate-600">
          Sin equipo técnico, sin consultores externos, sin perder convocatorias por desconocer que
          existían.
        </p>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
          {features.map((f) => (
            <article
              key={f.title}
              className="rounded-xl border border-slate-200 bg-white p-6 shadow-xs"
            >
              <h3 className="text-base font-bold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{f.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Sources() {
  return (
    <section className="px-6 py-16">
      <div className="mx-auto max-w-6xl">
        <h2 className="text-center text-2xl font-bold text-slate-900 sm:text-3xl">
          Fuentes oficiales en un solo lugar
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-slate-600">
          Datos ingresados directamente desde los portales del Estado, con verificación diaria de
          enlaces para que nunca hagas click en una URL rota.
        </p>
        <ul className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {PORTAL_SOURCES.map((p) => (
            <li
              key={p.code}
              className="rounded-lg border border-slate-200 bg-white p-4 text-center shadow-xs"
            >
              <p className="text-sm font-bold text-brand-700">{p.code}</p>
              <p className="mt-1 text-xs text-slate-500">{p.label}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Trust() {
  return (
    <section className="bg-slate-900 px-6 py-16 text-white">
      <div className="mx-auto max-w-4xl text-center">
        <h2 className="text-2xl font-bold sm:text-3xl">Trazabilidad auditable por diseño</h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-300">
          Cada evento que pasa por Kaptar (ver una convocatoria, crear un expediente, cambiar de
          estado) queda registrado con timestamp y hash SHA-256 encadenado. La factura del retainer
          es 100% impugnable frente al registro.
        </p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-white px-6 py-8">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 text-xs text-slate-500 sm:flex-row sm:justify-between">
        <p>Kaptar · {new Date().getFullYear()} · Hecho en Colombia</p>
        <nav className="flex gap-4">
          <Link to="/legal/terminos" className="hover:text-slate-700">
            Términos
          </Link>
          <Link to="/legal/privacidad" className="hover:text-slate-700">
            Privacidad
          </Link>
        </nav>
      </div>
    </footer>
  );
}
